import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Award, TrendingUp, ShieldCheck } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { useDocumentMeta } from "../hooks/use-document-meta";
import aboutContent from "../content/pages/about.json";

// Lucide icons referenced by name in the content file. New icons can be
// added here; an unknown name silently falls back to Award.
const ICON_MAP = { Award, TrendingUp, ShieldCheck };
const resolveIcon = (name) => ICON_MAP[name] || Award;

export default function About() {
  useDocumentMeta({
    title: "À propos — Grossiste fast-food & fournisseur frites surgelées depuis 2009",
    description:
      "Frites Dorées équipe la restauration rapide depuis 2009 : grossiste frites surgelées, viandes, sauces et emballages alimentaires professionnels. Labellisée BPI France Excellence. Près de 300 références B2B pour fast-foods en Île-de-France.",
    canonicalPath: "/a-propos",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const meta = aboutContent.meta || {};
  const pillars = aboutContent.pillars || [];
  const history = aboutContent.history || {};
  const timeline = history.timeline || [];
  const quote = aboutContent.quote || {};
  const cta = aboutContent.cta || {};

  // Title = N lines, with the line at `title_gold_line_index` rendered in gold.
  const titleLines = meta.title_lines || [];
  const goldIdx = typeof meta.title_gold_line_index === "number" ? meta.title_gold_line_index : -1;

  return (
    <main data-testid="about-page" className="bg-[#050505]">
      <PageHeader
        kicker={meta.kicker}
        title={
          <>
            {titleLines.map((line, i) => (
              <span key={i}>
                {i === goldIdx ? <span className="text-[#FFC300]">{line}</span> : line}
                {i < titleLines.length - 1 && <br />}
              </span>
            ))}
          </>
        }
        subtitle={meta.subtitle}
      />

      {/* Pillars */}
      <section className="px-6 md:px-10 lg:px-14 py-20 lg:py-28 border-b border-white/5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
          {pillars.map((p, i) => {
            const Icon = resolveIcon(p.icon);
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className="bg-[#050505] p-8 md:p-10"
              >
                <Icon size={28} className="text-[#FFC300]" />
                <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight mt-6">{p.title}</h3>
                <p className="font-body text-zinc-400 mt-3 leading-relaxed">{p.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Timeline */}
      <section className="px-6 md:px-10 lg:px-14 py-20 lg:py-32 border-b border-white/5">
        <div className="mb-16 max-w-3xl">
          <div className="tag-label mb-5 flex items-center gap-3">
            <span className="w-10 h-px bg-[#FFC300]"></span>
            <span>{history.kicker}</span>
          </div>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.95]">
            {history.title} {history.title_highlight && <><br /> <span className="text-[#FFC300]">{history.title_highlight}</span></>}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10">
          {timeline.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: (i % 2) * 0.1 }}
              className="border-t border-white/10 py-8 flex gap-6"
              data-testid={`timeline-${i}`}
            >
              <div className="w-24 shrink-0 font-display text-2xl md:text-3xl text-[#FFC300] tracking-tight">{t.year}</div>
              <div className="flex-1">
                <h3 className="font-display text-xl md:text-2xl uppercase tracking-tight">{t.label}</h3>
                <p className="font-body text-zinc-400 mt-2 leading-relaxed max-w-md">{t.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Quote */}
      <section className="px-6 md:px-10 lg:px-14 py-24 lg:py-32 border-b border-white/5">
        <motion.blockquote
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="max-w-5xl"
        >
          <p className="font-display text-3xl md:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.95]">
            {quote.line} {quote.line_highlight && <><br /><span className="text-[#FFC300]">{quote.line_highlight}</span></>}
          </p>
        </motion.blockquote>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-10 lg:px-14 py-20 lg:py-28 bg-[#FFC300] text-black">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-none">
            {(cta.title_lines || []).map((l, i, arr) => (
              <span key={i}>{l}{i < arr.length - 1 && <br />}</span>
            ))}
          </h2>
          <div className="flex gap-3">
            <Link to="/contact" className="btn-primary !bg-black !text-[#FFC300] hover:!bg-white hover:!text-black">
              {cta.primary_label || "Parler à un commercial"} <ArrowUpRight size={18} />
            </Link>
            <Link to="/catalogue" className="btn-secondary !border-black !text-black hover:!bg-black hover:!text-[#FFC300]">
              {cta.secondary_label || "Voir les 13 gammes"}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
